var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "nativewind/jsx-runtime";
import { useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { throttle } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { Image, ScrollView, View, } from 'react-native';
import { SccPressable } from '@/components/SccPressable';
import TabBar from '@/components/TabBar';
import { UploadProgressOverlay } from '@/components/UploadProgressOverlay';
import { showUploadFailedToast, useImageUploadWithProgress, } from '@/hooks/useImageUploadWithProgress';
import { SccButton } from '@/components/atoms';
import { MAX_NUMBER_OF_TAKEN_PHOTOS } from '@/constant/constant';
import { font } from '@/constant/font';
import { makeDoorTypeOptions } from '@/constant/options';
import { STAIR_COUNT_OPTIONS, STAIR_HEIGHT_OPTIONS, SLOPE_OPTIONS, HAS_ELEVATOR_OPTIONS, } from '@/constant/accessibility-options';
import { BuildingDoorDirectionTypeDto, StairInfo, } from '@/generated-sources/openapi';
import useAppComponents from '@/hooks/useAppComponents';
import { useFormExitConfirm } from '@/hooks/useFormExitConfirm';
import { REGISTER_TIMEOUT_MS, useIdempotencyKey, } from '@/hooks/useIdempotencyKey';
import { useKeyboardVisible } from '@/hooks/useKeyboardVisible';
import usePost from '@/hooks/usePost';
import { LogParamsProvider } from '@/logging/LogParamsProvider';
import Logger from '@/logging/Logger';
import FormExitConfirmBottomSheet from '@/modals/FormExitConfirmBottomSheet';
import { invalidateChallengeQueries } from '@/utils/challengeQueryCache';
import { updateSearchCacheForPlaceAsync } from '@/utils/SearchPlacesUtils';
import ToastUtils from '@/utils/ToastUtils';
import { SafeAreaWrapper } from '@/components/SafeAreaWrapper';
import { ScreenLayout } from '@/components/ScreenLayout';
import OptionsV2 from '../PlaceFormV2Screen/components/OptionsV2';
import PhotosV2 from '../PlaceFormV2Screen/components/PhotosV2';
import TextAreaV2 from '../PlaceFormV2Screen/components/TextAreaV2';
import { FORM_TOAST_OPTIONS, formImages } from '../PlaceFormV2Screen/constants';
import PlaceInfoSection from '../PlaceReviewFormScreen/sections/PlaceInfoSection';
import { pushItemsAtom } from '../SearchScreen/atoms/quest';
import * as S from './BuildingFormV2Screen.style';
const tabItems = [
    { label: '건물 입구 정보', value: 'entrance' },
    { label: '엘리베이터 정보', value: 'elevator' },
];
export default function BuildingFormV2Screen({ route, navigation, }) {
    const { place, building } = route.params;
    const form = useForm();
    const { api } = useAppComponents();
    const pushItems = useSetAtom(pushItemsAtom);
    const queryClient = useQueryClient();
    const { uploadImages, uploadProgress } = useImageUploadWithProgress();
    const getIdempotencyKey = useIdempotencyKey();
    const [currentTab, setCurrentTab] = useState('entrance');
    const isKeyboardVisible = useKeyboardVisible();
    // 뒤로가기 confirm
    const formExitConfirm = useFormExitConfirm(action => {
        navigation.dispatch(action);
    });
    const scrollViewRef = useRef(null);
    const entranceRef = useRef(null);
    const elevatorRef = useRef(null);
    const entranceY = useRef(0);
    const elevatorY = useRef(0);
    const isScrollingToSection = useRef(false);
    // Watch all required fields
    const entranceDirection = form.watch('entranceDirection');
    const doorDirectionEtcComment = form.watch('doorDirectionEtcComment');
    const enterancePhotos = form.watch('enterancePhotos');
    const hasStairs = form.watch('hasStairs');
    const stairInfo = form.watch('stairInfo');
    const entranceStairHeightLevel = form.watch('entranceStairHeightLevel');
    const hasSlope = form.watch('hasSlope');
    const doorTypes = form.watch('doorTypes');
    const hasElevator = form.watch('hasElevator');
    const elevatorPhotos = form.watch('elevatorPhotos');
    const elevatorHasStairs = form.watch('elevatorHasStairs');
    const elevatorStairInfo = form.watch('elevatorStairInfo');
    const elevatorStairHeightLevel = form.watch('elevatorStairHeightLevel');
    const elevatorHasSlope = form.watch('elevatorHasSlope');
    // Reset elevator related fields when hasElevator changes to false
    useEffect(() => {
        if (hasElevator === false) {
            form.setValue('elevatorPhotos', undefined);
            form.setValue('elevatorHasStairs', undefined);
            form.setValue('elevatorStairInfo', undefined);
            form.setValue('elevatorStairHeightLevel', undefined);
            form.setValue('elevatorHasSlope', undefined);
        }
    }, [hasElevator, form]);
    // Reset entrance stair related fields when hasStairs changes to false
    useEffect(() => {
        if (hasStairs === false) {
            form.setValue('stairInfo', undefined);
            form.setValue('entranceStairHeightLevel', undefined);
        }
    }, [hasStairs, form]);
    // Reset elevator stair related fields when elevatorHasStairs changes to false
    useEffect(() => {
        if (elevatorHasStairs === false) {
            form.setValue('elevatorStairInfo', undefined);
            form.setValue('elevatorStairHeightLevel', undefined);
        }
    }, [elevatorHasStairs, form]);
    // Reset entrance stair height when stairInfo is not One
    useEffect(() => {
        if (stairInfo && stairInfo !== StairInfo.One) {
            form.setValue('entranceStairHeightLevel', undefined);
        }
    }, [stairInfo, form]);
    // Reset elevator stair height when elevatorStairInfo is not One
    useEffect(() => {
        if (elevatorStairInfo && elevatorStairInfo !== StairInfo.One) {
            form.setValue('elevatorStairHeightLevel', undefined);
        }
    }, [elevatorStairInfo, form]);
    // Reset doorDirectionEtcComment when entranceDirection is not 'etc'
    useEffect(() => {
        if (entranceDirection && entranceDirection !== 'etc') {
            form.setValue('doorDirectionEtcComment', undefined);
        }
    }, [entranceDirection, form]);
    // 유효성 검사 및 첫 번째 에러 키 반환
    const validateAndGetError = () => {
        // 출입구 위치는 필수
        if (!entranceDirection) {
            return 'entranceDirection';
        }
        // 기타 선택 시 출입구 이름 필수
        if (entranceDirection === 'etc' && !(doorDirectionEtcComment === null || doorDirectionEtcComment === void 0 ? void 0 : doorDirectionEtcComment.trim())) {
            return 'doorDirectionEtcComment';
        }
        // 입구 사진은 필수
        if (!enterancePhotos || enterancePhotos.length === 0) {
            return 'enterancePhotos';
        }
        // 계단 여부는 필수 (boolean)
        if (typeof hasStairs !== 'boolean') {
            return 'hasStairs';
        }
        // 계단이 있을 경우 계단 정보 필수
        if (hasStairs && !stairInfo) {
            return 'stairInfo';
        }
        // 계단이 1칸일 경우 높이 정보 필수
        if (hasStairs && stairInfo === StairInfo.One && !entranceStairHeightLevel) {
            return 'entranceStairHeightLevel';
        }
        // 경사로 여부는 필수 (boolean)
        if (typeof hasSlope !== 'boolean') {
            return 'hasSlope';
        }
        // 출입문 종류는 필수
        if (!doorTypes || doorTypes.length === 0) {
            return 'doorTypes';
        }
        // 엘리베이터 여부는 필수 (boolean)
        if (typeof hasElevator !== 'boolean') {
            return 'hasElevator';
        }
        // 엘리베이터가 있을 경우 추가 검증
        if (hasElevator) {
            // 엘리베이터 사진은 필수
            if (!elevatorPhotos || elevatorPhotos.length === 0) {
                return 'elevatorPhotos';
            }
            // 엘리베이터 계단 여부는 필수 (boolean)
            if (typeof elevatorHasStairs !== 'boolean') {
                return 'elevatorHasStairs';
            }
            // 계단이 있을 경우 계단 정보 필수
            if (elevatorHasStairs && !elevatorStairInfo) {
                return 'elevatorStairInfo';
            }
            // 계단이 1칸일 경우 높이 정보 필수
            if (elevatorHasStairs &&
                elevatorStairInfo === StairInfo.One &&
                !elevatorStairHeightLevel) {
                return 'elevatorStairHeightLevel';
            }
            // 경사로 여부는 필수 (boolean)
            if (typeof elevatorHasSlope !== 'boolean') {
                return 'elevatorHasSlope';
            }
        }
        return null;
    };
    // Measure section positions on mount
    useEffect(() => {
        const timer = setTimeout(() => {
            var _a, _b;
            (_a = entranceRef.current) === null || _a === void 0 ? void 0 : _a.measureLayout(scrollViewRef.current, (_x, y) => {
                entranceY.current = y - 50;
            }, () => { });
            (_b = elevatorRef.current) === null || _b === void 0 ? void 0 : _b.measureLayout(scrollViewRef.current, (_x, y) => {
                elevatorY.current = y - 50;
            }, () => { });
        }, 100);
        return () => clearTimeout(timer);
    }, []);
    const updateTabBasedOnScroll = useMemo(() => throttle((scrollY, contentHeight, layoutHeight) => {
        // Check if scrolled to bottom
        const isAtBottom = scrollY + layoutHeight >= contentHeight - 50;
        // Determine which section is currently visible
        if (scrollY < elevatorY.current - 100 && !isAtBottom) {
            if (currentTab !== 'entrance') {
                setCurrentTab('entrance');
            }
        }
        else {
            if (currentTab !== 'elevator') {
                setCurrentTab('elevator');
            }
        }
    }, 100), [currentTab]);
    const handleScroll = (event) => {
        if (isScrollingToSection.current) {
            return;
        }
        const scrollY = event.nativeEvent.contentOffset.y;
        const contentHeight = event.nativeEvent.contentSize.height;
        const layoutHeight = event.nativeEvent.layoutMeasurement.height;
        if (typeof scrollY !== 'number') {
            return;
        }
        updateTabBasedOnScroll(scrollY, contentHeight, layoutHeight);
    };
    const handleTabChange = (tab) => {
        var _a;
        setCurrentTab(tab);
        isScrollingToSection.current = true;
        const targetY = tab === 'entrance' ? entranceY.current : elevatorY.current;
        (_a = scrollViewRef.current) === null || _a === void 0 ? void 0 : _a.scrollTo({ y: targetY, animated: true });
        setTimeout(() => {
            isScrollingToSection.current = false;
        }, 500);
    };
    function submit() {
        const errorKey = validateAndGetError();
        if (errorKey) {
            noticeError(errorKey);
            return;
        }
        const values = form.watch();
        registerBuilding(values);
    }
    const submitMutation = usePost(['BuildingFormV2', 'Submit'], (_a) => __awaiter(this, [_a], void 0, function* ({ values, uploaded, requestId, }) {
        return submitRegistration(api, queryClient, place.id, building.id, values, uploaded, requestId);
    }));
    const registerBuilding = useMemo(() => throttle((values) => __awaiter(this, void 0, void 0, function* () {
        if (submitMutation.isPending) {
            return;
        }
        // 사진 URL 은 업로드마다 달라지므로 업로드 전 입력(로컬 사진 포함)으로 키를 정한다.
        const requestId = getIdempotencyKey(values);
        let uploaded;
        try {
            uploaded = yield uploadAllPhotos(api, values, uploadImages);
        }
        catch (e) {
            showUploadFailedToast(e);
            return;
        }
        const registered = yield submitMutation.mutateAsync({
            values,
            uploaded,
            requestId,
        });
        if (!registered.success) {
            return;
        }
        if (registered.data) {
            pushItems(registered.data);
        }
        navigation.navigate('RegistrationComplete', {
            target: 'building',
            placeInfo: {
                place,
                building,
            },
        });
    }), 1000), [
        api,
        place,
        building,
        navigation,
        uploadImages,
        getIdempotencyKey,
        submitMutation,
        queryClient,
        pushItems,
    ]);
    function noticeError(errorKey) {
        const toastOptions = FORM_TOAST_OPTIONS;
        switch (errorKey) {
            case 'entranceDirection':
                ToastUtils.show('출입구 위치를 선택해주세요.', toastOptions);
                break;
            case 'doorDirectionEtcComment':
                ToastUtils.show('출입구 이름을 입력해주세요.', toastOptions);
                break;
            case 'enterancePhotos':
                ToastUtils.show('입구 사진을 등록해주세요.', toastOptions);
                break;
            case 'stairInfo':
            case 'hasStairs':
            case 'entranceStairHeightLevel':
                ToastUtils.show('계단 정보를 입력해주세요.', toastOptions);
                break;
            case 'hasElevator':
                ToastUtils.show('엘리베이터 정보를 입력해주세요.', toastOptions);
                break;
            case 'elevatorPhotos':
                ToastUtils.show('엘리베이터 사진을 등록해주세요.', toastOptions);
                break;
            case 'elevatorHasStairs':
            case 'elevatorStairHeightLevel':
            case 'elevatorStairInfo':
                ToastUtils.show('엘리베이터 계단 정보를 입력해주세요.', toastOptions);
                break;
            case 'hasSlope':
                ToastUtils.show('경사로 정보를 입력해주세요.', toastOptions);
                break;
            case 'elevatorHasSlope':
                ToastUtils.show('엘리베이터 경사로 정보를 입력해주세요.', toastOptions);
                break;
            case 'doorTypes':
                ToastUtils.show('출입문 종류를 선택해주세요.', toastOptions);
                break;
            default:
                ToastUtils.show('필수 정보를 입력해주세요.', toastOptions);
        }
    }
    return (_jsx(LogParamsProvider, { params: { building_id: building.id }, children: _jsxs(FormProvider, Object.assign({}, form, { children: [_jsxs(ScreenLayout, { isHeaderVisible: true, children: [_jsx(S.HeaderBorder, {}), _jsxs(ScrollView, { ref: scrollViewRef, stickyHeaderIndices: [2], onScroll: handleScroll, scrollEventThrottle: 16, children: [_jsx(PlaceInfoSection, { target: "building", name: place.name, address: place.name + ' 장소가 있는 건물' }), _jsx(S.SectionSeparator, {}), _jsx(S.TabBarWrapper, { children: _jsx(TabBar, { items: tabItems, current: currentTab, onChange: handleTabChange }) }), _jsxs(S.FormContainer, { children: [_jsxs(View, { ref: entranceRef, collapsable: false, style: { gap: 60 }, children: [_jsxs(S.SubSection, { children: [_jsxs(S.QuestionSection, { children: [_jsx(S.SectionLabel, { children: "\uAC74\uBB3C \uC785\uAD6C \uC815\uBCF4" }), _jsxs(View, { style: { gap: 2 }, children: [_jsxs(S.QuestionText, { children: ["\uB4F1\uB85D\uD558\uB824\uB294 \uCD9C\uC785\uAD6C \uC704\uCE58\uB97C \uC54C\uB824\uC8FC\uC138\uC694", ' ', _jsx(S.RequiredMark, { children: "*" })] }), _jsx(S.Hint, { children: "\uC811\uADFC\uC131 \uC88B\uC740 \uCD9C\uC785\uAD6C\uB97C \uC785\uB825\uD558\uBA74 \uC88B\uC544\uC694" })] })] }), _jsx(Controller, { name: "entranceDirection", rules: { required: true }, render: ({ field }) => (_jsxs(View, { style: { gap: 12 }, children: [_jsxs(S.EntranceDirectionContainer, { children: [_jsxs(S.EntranceDirectionOption, { children: [_jsx(S.EntranceDirectionImageContainer, { disabled: field.value && field.value !== 'road', children: _jsx(Image, { source: formImages.buildingEntrance.road, style: { width: '100%', height: '100%' }, resizeMode: "cover" }) }), _jsx(OptionsV2, { value: field.value, columns: 1, options: [{ label: '도로 방향 문', value: 'road' }], onSelect: field.onChange })] }), _jsxs(S.EntranceDirectionOption, { children: [_jsx(S.EntranceDirectionImageContainer, { disabled: field.value && field.value !== 'parking', children: _jsx(Image, { source: formImages.buildingEntrance.parking, style: { width: '100%', height: '100%' }, resizeMode: "cover" }) }), _jsx(OptionsV2, { value: field.value, columns: 1, options: [
                                                                                            { label: '주차장 쪽 연결 문', value: 'parking' },
                                                                                        ], onSelect: field.onChange })] })] }), _jsx(OptionsV2, { value: field.value, columns: 1, options: [{ label: '그 외 출입문', value: 'etc' }], onSelect: field.onChange })] })) }), entranceDirection === 'etc' && (_jsxs(S.SubQuestion, { children: [_jsxs(S.QuestionText, { children: ["\uADF8 \uC678 \uCD9C\uC785\uBB38 \uC704\uCE58\uB97C \uC54C\uB824\uC8FC\uC138\uC694", ' ', _jsx(S.RequiredMark, { children: "*" })] }), _jsx(Controller, { name: "doorDirectionEtcComment", rules: { required: entranceDirection === 'etc' }, render: ({ field }) => (_jsx(S.TextInputContainer, { children: _jsx(S.StyledTextInput, { placeholder: "\uC608\uC2DC) \uC11C\uBB38 1, Gate 8, \uC9C0\uD558 \uC8FC\uCC28\uC7A5 2\uCE35 \uC0C1\uAC00\uBC29\uD5A5", value: field.value, onChangeText: field.onChange }) })) })] }))] }), _jsxs(S.SubSection, { children: [_jsxs(View, { style: { gap: 2 }, children: [_jsxs(S.Label, { children: ["\uAC74\uBB3C \uC785\uAD6C \uC0AC\uC9C4\uC744 \uCC0D\uC5B4\uC8FC\uC138\uC694", ' ', _jsx(S.RequiredMark, { children: "*" })] }), _jsx(S.Hint, { children: "\uCD5C\uB300 3\uC7A5\uAE4C\uC9C0 \uB4F1\uB85D \uAC00\uB2A5\uD574\uC694" })] }), _jsx(Controller, { name: "enterancePhotos", rules: { required: true }, render: ({ field }) => {
                                                                var _a;
                                                                return (_jsx(PhotosV2, { value: (_a = field.value) !== null && _a !== void 0 ? _a : [], onChange: field.onChange, target: "buildingEntrance", maxPhotos: MAX_NUMBER_OF_TAKEN_PHOTOS }));
                                                            } })] }), _jsxs(S.SubSection, { children: [_jsxs(S.Label, { children: ["\uAC74\uBB3C \uC785\uAD6C\uC5D0 \uACC4\uB2E8\uC774 \uC788\uB098\uC694?", ' ', _jsx(S.RequiredMark, { children: "*" })] }), _jsxs(S.OptionsGroup, { children: [_jsx(Controller, { name: "hasStairs", rules: { validate: v => typeof v === 'boolean' }, render: ({ field }) => (_jsx(OptionsV2, { value: field.value, options: [
                                                                            { label: '있어요', value: true },
                                                                            { label: '없어요', value: false },
                                                                        ], onSelect: field.onChange })) }), form.watch('hasStairs') && (_jsx(Controller, { name: "stairInfo", rules: { required: true }, render: ({ field }) => (_jsx(OptionsV2, { value: field.value, columns: 3, options: STAIR_COUNT_OPTIONS, onSelect: field.onChange })) }))] }), _jsx(S.GuideButton, { children: _jsx(SccPressable, { elementName: "building_entrance_stair_guide", onPress: () => navigation.navigate('Webview', {
                                                                    fixedTitle: '계단 기준 알아보기',
                                                                    url: 'https://agnica.notion.site/8312cc653a8f4b9aa8bc920bbd668218',
                                                                }), children: _jsxs(S.GuideText, { children: ["\uACC4\uB2E8 \uAE30\uC900 \uC54C\uC544\uBCF4\uAE30 ", '>'] }) }) })] }), form.watch('hasStairs') &&
                                                    form.watch('stairInfo') === StairInfo.One && (_jsxs(S.SubSection, { children: [_jsxs(S.Label, { children: ["\uACC4\uB2E8 1\uCE78\uC758 \uB192\uC774\uB97C \uC54C\uB824\uC8FC\uC138\uC694", ' ', _jsx(S.RequiredMark, { children: "*" })] }), _jsx(S.MeasureGuide, { children: _jsx(Image, { source: formImages.stair, style: { width: '100%', height: '100%' } }) }), _jsx(View, { style: { gap: 16 }, children: _jsx(Controller, { name: "entranceStairHeightLevel", rules: { required: true }, render: ({ field }) => (_jsx(OptionsV2, { value: field.value, options: STAIR_HEIGHT_OPTIONS, onSelect: field.onChange })) }) })] })), _jsxs(S.SubSection, { children: [_jsxs(S.Label, { children: ["\uAC74\uBB3C \uC785\uAD6C\uC5D0 \uACBD\uC0AC\uB85C\uAC00 \uC788\uB098\uC694?", ' ', _jsx(S.RequiredMark, { children: "*" })] }), _jsx(Controller, { name: "hasSlope", rules: { validate: v => typeof v === 'boolean' }, render: ({ field }) => (_jsx(OptionsV2, { value: field.value, options: SLOPE_OPTIONS, onSelect: field.onChange })) }), _jsx(S.GuideButton, { children: _jsx(SccPressable, { elementName: "building_entrance_slope_guide", onPress: () => navigation.navigate('Webview', {
                                                                    fixedTitle: '경사로 기준 알아보기',
                                                                    url: 'https://agnica.notion.site/6f64035a062f41e28745faa4e7bd0770',
                                                                }), children: _jsxs(S.GuideText, { children: ["\uACBD\uC0AC\uB85C \uAE30\uC900 \uC54C\uC544\uBCF4\uAE30 ", '>'] }) }) })] }), _jsxs(S.SubSection, { children: [_jsxs(View, { style: { gap: 2 }, children: [_jsxs(S.Label, { children: ["\uCD9C\uC785\uBB38\uC740 \uC5B4\uB5A4 \uC885\uB958\uC778\uAC00\uC694?", ' ', _jsx(S.RequiredMark, { children: "*" })] }), _jsx(S.Hint, { children: "\uC911\uBCF5\uC120\uD0DD\uC774 \uAC00\uB2A5\uD574\uC694" })] }), _jsx(Controller, { name: "doorTypes", rules: { required: true }, render: ({ field }) => {
                                                                var _a;
                                                                return (_jsx(OptionsV2.Multiple, { values: field.value, columns: 2, options: makeDoorTypeOptions((_a = form.watch('doorTypes')) !== null && _a !== void 0 ? _a : []), onSelect: field.onChange }));
                                                            } })] }), _jsxs(S.SubSection, { children: [_jsx(S.Label, { children: "\uB354 \uB3C4\uC6C0\uC774 \uB420 \uC815\uBCF4\uAC00 \uC788\uB2E4\uBA74 \uC54C\uB824\uC8FC\uC138\uC694" }), _jsx(Controller, { name: "comment", render: ({ field }) => (_jsx(TextAreaV2, { placeholder: "\uC608\uC2DC) \uD6C4\uBB38\uC5D0\uB294 \uACC4\uB2E8\uC774 \uC5C6\uC5B4 \uD3B8\uD558\uAC8C \uAC08 \uC218 \uC788\uC2B5\uB2C8\uB2E4", value: field.value, onChangeText: field.onChange })) })] })] }), _jsxs(View, { ref: elevatorRef, collapsable: false, style: { gap: 48, marginTop: 80 }, children: [_jsxs(S.SubSection, { children: [_jsxs(S.QuestionSection, { children: [_jsx(S.SectionLabel, { children: "\uC5D8\uB9AC\uBCA0\uC774\uD130 \uC815\uBCF4" }), _jsxs(S.QuestionText, { children: ["\uAC74\uBB3C\uC5D0 \uC5D8\uB9AC\uBCA0\uC774\uD130\uAC00 \uC788\uB098\uC694?", ' ', _jsx(S.RequiredMark, { children: "*" })] })] }), _jsx(Controller, { name: "hasElevator", rules: { validate: v => typeof v === 'boolean' }, render: ({ field }) => (_jsx(OptionsV2, { value: field.value, options: HAS_ELEVATOR_OPTIONS, onSelect: field.onChange })) })] }), form.watch('hasElevator') && (_jsxs(_Fragment, { children: [_jsxs(S.SubSection, { children: [_jsxs(View, { style: { gap: 2 }, children: [_jsxs(S.Label, { children: ["\uC5D8\uB9AC\uBCA0\uC774\uD130 \uC0AC\uC9C4\uC744 \uCC0D\uC5B4\uC8FC\uC138\uC694", ' ', _jsx(S.RequiredMark, { children: "*" })] }), _jsx(S.Hint, { children: "\uCD5C\uB300 3\uC7A5\uAE4C\uC9C0 \uB4F1\uB85D \uAC00\uB2A5\uD574\uC694" })] }), _jsx(Controller, { name: "elevatorPhotos", rules: { required: true }, render: ({ field }) => {
                                                                        var _a;
                                                                        return (_jsx(PhotosV2, { value: (_a = field.value) !== null && _a !== void 0 ? _a : [], onChange: field.onChange, target: "elevator", maxPhotos: MAX_NUMBER_OF_TAKEN_PHOTOS }));
                                                                    } })] }), _jsxs(S.SubSection, { children: [_jsxs(S.Label, { children: ["\uC5D8\uB9AC\uBCA0\uC774\uD130\uAE4C\uC9C0 \uAC00\uB294 \uAE38\uC5D0 \uACC4\uB2E8\uC774 \uC788\uB098\uC694?", ' ', _jsx(S.RequiredMark, { children: "*" })] }), _jsxs(S.OptionsGroup, { children: [_jsx(Controller, { name: "elevatorHasStairs", rules: { validate: v => typeof v === 'boolean' }, render: ({ field }) => (_jsx(OptionsV2, { value: field.value, options: [
                                                                                    { label: '있어요', value: true },
                                                                                    { label: '없어요', value: false },
                                                                                ], onSelect: field.onChange })) }), form.watch('elevatorHasStairs') && (_jsx(Controller, { name: "elevatorStairInfo", rules: { required: true }, render: ({ field }) => (_jsx(OptionsV2, { value: field.value, columns: 3, options: STAIR_COUNT_OPTIONS, onSelect: field.onChange })) }))] })] }), form.watch('elevatorHasStairs') &&
                                                            form.watch('elevatorStairInfo') === StairInfo.One && (_jsxs(S.SubSection, { children: [_jsxs(S.Label, { children: ["\uACC4\uB2E8 1\uCE78\uC758 \uB192\uC774\uB97C \uC54C\uB824\uC8FC\uC138\uC694", ' ', _jsx(S.RequiredMark, { children: "*" })] }), _jsx(S.MeasureGuide, { children: _jsx(Image, { source: formImages.stair, style: { width: '100%', height: '100%' } }) }), _jsx(View, { style: { gap: 16 }, children: _jsx(Controller, { name: "elevatorStairHeightLevel", rules: { required: true }, render: ({ field }) => (_jsx(OptionsV2, { value: field.value, options: STAIR_HEIGHT_OPTIONS, onSelect: field.onChange })) }) })] })), _jsxs(S.SubSection, { children: [_jsxs(S.Label, { children: ["\uC5D8\uB9AC\uBCA0\uC774\uD130\uAE4C\uC9C0 \uAC00\uB294 \uAE38\uC5D0 \uACBD\uC0AC\uB85C\uAC00 \uC788\uB098\uC694?", ' ', _jsx(S.RequiredMark, { children: "*" })] }), _jsx(Controller, { name: "elevatorHasSlope", render: ({ field }) => (_jsx(OptionsV2, { value: field.value, options: SLOPE_OPTIONS, onSelect: field.onChange })) })] })] }))] })] })] }), _jsx(SafeAreaWrapper, { edges: isKeyboardVisible ? [] : ['bottom'], children: _jsx(S.SubmitButtonWrapper, { children: _jsx(SccButton, { text: "\uB4F1\uB85D\uD558\uAE30", buttonColor: "brandColor", fontFamily: font.pretendardSemibold, onPress: submit, elementName: "building_form_submit", style: { borderRadius: 12 } }) }) })] }), _jsx(FormExitConfirmBottomSheet, { isVisible: formExitConfirm.isVisible, onConfirm: formExitConfirm.onConfirm, onCancel: formExitConfirm.onCancel }), _jsx(UploadProgressOverlay, Object.assign({}, uploadProgress))] })) }));
}
function mapEntranceDirectionToDoorDirectionType(entranceDirection) {
    switch (entranceDirection) {
        case 'road':
            return BuildingDoorDirectionTypeDto.RoadDirection;
        case 'parking':
            return BuildingDoorDirectionTypeDto.ParkingDirection;
        case 'etc':
            return BuildingDoorDirectionTypeDto.Etc;
        default:
            return BuildingDoorDirectionTypeDto.Etc;
    }
}
function uploadAllPhotos(api, values, uploadImages) {
    return __awaiter(this, void 0, void 0, function* () {
        const startImageUpload = Date.now();
        const enteranceImages = yield uploadImages(api, values.enterancePhotos, undefined, '입구 사진');
        const elevatorImages = yield uploadImages(api, values.elevatorPhotos, undefined, '엘리베이터 사진');
        return {
            enteranceImages,
            elevatorImages,
            durationMillisImageUpload: Date.now() - startImageUpload,
        };
    });
}
function submitRegistration(api, queryClient, placeId, buildingId, values, uploaded, requestId) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e, _f;
        const startTotal = Date.now();
        const imageCount = ((_b = (_a = values.enterancePhotos) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) +
            ((_d = (_c = values.elevatorPhotos) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0);
        const { enteranceImages, elevatorImages, durationMillisImageUpload } = uploaded;
        try {
            const startApiCall = Date.now();
            const res = yield api.registerBuildingAccessibilityV2Post({
                buildingId,
                doorDirectionType: mapEntranceDirectionToDoorDirectionType(values.entranceDirection),
                doorDirectionEtcComment: values.entranceDirection === 'etc' &&
                    ((_e = values.doorDirectionEtcComment) === null || _e === void 0 ? void 0 : _e.trim())
                    ? values.doorDirectionEtcComment.trim()
                    : undefined,
                entranceStairInfo: values.hasStairs ? values.stairInfo : StairInfo.None,
                entranceStairHeightLevel: values.entranceStairHeightLevel,
                entranceDoorTypes: values.doorTypes,
                hasSlope: values.hasSlope || false,
                entranceImageUrls: enteranceImages,
                hasElevator: values.hasElevator || false,
                elevatorAccessibility: values.hasElevator
                    ? {
                        imageUrls: elevatorImages,
                        stairInfo: values.elevatorHasStairs
                            ? values.elevatorStairInfo
                            : StairInfo.None,
                        stairHeightLevel: values.elevatorStairHeightLevel,
                        hasSlope: typeof values.elevatorHasSlope === 'boolean'
                            ? values.elevatorHasSlope
                            : undefined,
                    }
                    : undefined,
                comment: values.comment || undefined,
            }, requestId, { timeout: REGISTER_TIMEOUT_MS });
            const durationApiCall = Date.now() - startApiCall;
            yield Logger.logAccessibilityRegistration({
                type: 'building',
                durationMillisImageUpload,
                durationMillisApiCall: durationApiCall,
                durationMillisTotal: durationMillisImageUpload + (Date.now() - startTotal),
                imageCount,
                success: true,
            });
            queryClient.invalidateQueries({
                queryKey: ['PlaceDetailV2', placeId, 'Accessibility'],
            });
            queryClient.invalidateQueries({
                queryKey: ['PlaceDetailV2', placeId],
            });
            // 정복(챌린지 기여)으로 바뀐 남은 매장 목록·챌린지 진척도·홈 카드 갱신.
            invalidateChallengeQueries(queryClient);
            updateSearchCacheForPlaceAsync(api, queryClient, placeId);
            return {
                success: true,
                data: (_f = res.data.contributedChallengeInfos) === null || _f === void 0 ? void 0 : _f.flatMap(info => info.completedQuestsByContribution.map(quest => ({
                    challengeId: info.challenge.id,
                    type: quest.completeStampType,
                    title: quest.title,
                }))),
            };
        }
        catch (error) {
            yield Logger.logAccessibilityRegistration({
                type: 'building',
                durationMillisImageUpload,
                durationMillisApiCall: Date.now() - startTotal,
                durationMillisTotal: durationMillisImageUpload + (Date.now() - startTotal),
                imageCount,
                success: false,
            });
            ToastUtils.showOnApiError(error);
            return {
                success: false,
            };
        }
    });
}
